import React,{useState} from 'react';
import {format} from 'date-fns';
import {motion,AnimatePresence} from 'framer-motion';
import {supabase} from '../../supabaseClient';
import {processVideoUrl} from '../../utils/videoUtils';
import SafeIcon from '../../common/SafeIcon';
import TextLesson from './TextLesson';
import {triggerZapierEvent} from '../../utils/zapier';

const parseLocalDate=(dateStr)=> {
  if (!dateStr) return null;
  const [year,month,day]=dateStr.split('-').map(Number);
  return new Date(year,month - 1,day);
};

const TaskCard=({task,onUpdate,onRescheduleRestDay,isRescheduling,dayLabel,otherDaysInWeek})=> {
  const [isUpdating,setIsUpdating]=useState(false);
  const [localVideoUrls,setLocalVideoUrls]=useState(task.video_submission_url ? [task.video_submission_url] : []);
  const [completedCheckboxes,setCompletedCheckboxes]=useState(task.completed_checkboxes || []);
  const [isDemoVideosExpanded,setIsDemoVideosExpanded]=useState(false);
  const [showRescheduleMenu,setShowRescheduleMenu]=useState(false);
  const [expandedVideoUrl,setExpandedVideoUrl]=useState(null);

  const taskDate=parseLocalDate(task.task_date);

  const updateTask=async (updates)=> {
    setIsUpdating(true);
    try {
      const {error}=await supabase.from('training_tasks').update(updates).eq('id',task.id);
      if (!error) onUpdate();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCheckboxToggle=(idx)=> {
    const newOnes=completedCheckboxes.includes(idx) ? completedCheckboxes.filter(i=> i !==idx) : [...completedCheckboxes,idx];
    setCompletedCheckboxes(newOnes);
    updateTask({completed_checkboxes: newOnes});
  };

  const handleCompletionToggle=async ()=> {
    setIsUpdating(true);
    try {
      const {error}=await supabase
        .from('training_tasks')
        .update({is_completed: !task.is_completed})
        .eq('id',task.id);
      
      if (!error && !task.is_completed) {
        const {data: planData}=await supabase
          .from('training_plans')
          .select('training_clients(trainer_id,dog_name,owner_name)')
          .eq('id',task.plan_id)
          .single();
        
        if (planData?.training_clients?.trainer_id) {
          triggerZapierEvent(planData.training_clients.trainer_id,'task_completed',{
            dog_name: planData.training_clients.dog_name,
            owner_name: planData.training_clients.owner_name,
            task_title: task.title,
            completed_at: new Date().toISOString()
          });
        }
      }
      if (!error) onUpdate();
    } catch (err) {
      console.error(err);
    } finally {
      setIsUpdating(false);
    }
  };

  if (task.is_rest_day) {
    return (
      <div className={`bg-white rounded-xl border ${task.is_completed ? 'border-green-200 shadow-sm' : 'border-gray-200 shadow-sm'} mb-4 p-3 sm:p-5 relative ${showRescheduleMenu ? 'z-50' : 'z-10'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
              <SafeIcon name="Clock" className="w-3 h-3" />
              <span>{taskDate ? format(taskDate,'MMM d,yyyy') : `Day ${task.day_number}`}</span>
              <span className="text-green-500 font-black ml-2">• Rest Day</span>
            </div>
            <h3 className={`text-lg font-bold text-gray-900 ${task.is_completed ? 'text-green-800' : ''}`}>{task.title}</h3>
            {task.description && <p className="text-gray-500 text-sm mt-2 leading-relaxed">{task.description}</p>}
            
            <div className="mt-4 flex items-center gap-3">
              <div className="relative">
                <button 
                  onClick={()=> setShowRescheduleMenu(!showRescheduleMenu)}
                  disabled={isRescheduling}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-indigo-100 transition-all"
                >
                  {isRescheduling ? (
                    <div className="w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <SafeIcon name="Calendar" className="w-3 h-3" />
                  )}
                  Move Rest Day
                </button>
                {showRescheduleMenu && (
                  <div className="absolute left-0 top-full mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-[60] animate-in fade-in slide-in-from-top-2">
                    <p className="px-4 py-2 text-[9px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-50 mb-1">Move to:</p>
                    {otherDaysInWeek.filter(d=> d.dayNumber !==task.day_number).map(day=> (
                      <button 
                        key={day.dayNumber}
                        onClick={()=> {onRescheduleRestDay(task.id,day.dayNumber);setShowRescheduleMenu(false);}}
                        className="w-full text-left px-4 py-2 text-xs font-bold text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="text-[10px] text-gray-300 font-medium italic">Swaps with training task</span>
            </div>
          </div>
          <button 
            onClick={handleCompletionToggle}
            disabled={isUpdating}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${task.is_completed ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
          >
            {isUpdating ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <SafeIcon name="Check" className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border ${task.is_completed ? 'border-green-200 shadow-sm' : 'border-gray-200 shadow-sm'} mb-4 p-3 sm:p-5 relative z-10`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            <SafeIcon name="Clock" className="w-3 h-3" />
            <span>{taskDate ? format(taskDate,'MMM d,yyyy') : `Day ${task.day_number}`}</span>
          </div>
          <h3 className={`text-lg font-bold text-gray-900 ${task.is_completed ? 'text-green-800' : ''}`}>{task.title}</h3>
        </div>
        <button 
          onClick={handleCompletionToggle}
          disabled={isUpdating}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${task.is_completed ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
        >
          {isUpdating ? (
            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <SafeIcon name="Check" className="w-6 h-6" />
          )}
        </button>
      </div>

      {task.description && <p className="text-gray-600 text-sm leading-relaxed mb-4">{task.description}</p>}

      <TextLesson task={task} />

      {task.resource_url && (
        <div className="mb-6">
          <a 
            href={task.resource_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 bg-indigo-50 border border-indigo-100 text-indigo-700 px-4 py-3.5 rounded-xl text-sm font-bold hover:bg-indigo-100 transition-colors group shadow-sm"
          >
            <SafeIcon name="Link" className="w-4 h-4 text-indigo-500" />
            <span className="flex-1 truncate">External Training Resource</span>
            <SafeIcon name="ExternalLink" className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
          </a>
        </div>
      )}

      {task.task_checkboxes?.length > 0 && (
        <div className="mb-4 space-y-2">
          {task.task_checkboxes.map((cb,i)=> (
            <label key={i} className="flex items-start gap-3 cursor-pointer group">
              <input 
                type="checkbox" 
                checked={completedCheckboxes.includes(i)}
                onChange={()=> handleCheckboxToggle(i)}
                className="mt-0.5 w-4 h-4 text-indigo-600 border-gray-300 rounded"
              />
              <span className={`text-sm ${completedCheckboxes.includes(i) ? 'text-gray-500 line-through' : 'text-gray-700'}`}>{cb}</span>
            </label>
          ))}
        </div>
      )}

      {task.demo_videos?.length > 0 && (
        <div className="mb-6 bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 shadow-inner">
          <button 
            onClick={()=> setIsDemoVideosExpanded(!isDemoVideosExpanded)}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2 text-indigo-600">
              <SafeIcon name="Video" className="w-5 h-5" />
              <span className="text-sm font-black text-gray-800">{task.demo_video_title || 'Demo Videos'} ({task.demo_videos.length})</span>
            </div>
            <SafeIcon name={isDemoVideosExpanded ? "ChevronUp" : "ChevronDown"} className="w-4 h-4 text-gray-400" />
          </button>
          
          {isDemoVideosExpanded && (
            <div className="border-t border-gray-100">
              <div className="flex flex-col">
                {task.demo_videos.map((url,i)=> {
                  const embed=processVideoUrl(url);
                  return embed ? (
                    <div key={i} className="relative aspect-video bg-black w-full border-b border-gray-100 last:border-0 group">
                      <iframe 
                        src={embed} 
                        className="absolute inset-0 w-full h-full" 
                        allow="fullscreen;picture-in-picture;encrypted-media" 
                        allowFullScreen 
                      />
                      <button 
                        onClick={() => setExpandedVideoUrl(embed)}
                        className="absolute top-3 right-3 p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg opacity-100 sm:opacity-0 group-hover:opacity-100 transition-all z-10 flex items-center gap-1.5"
                        title="Expand Video"
                      >
                        <SafeIcon name="Video" className="w-4 h-4" />
                        <span className="text-[10px] font-black uppercase tracking-widest pr-1 hidden sm:inline">Expand</span>
                      </button>
                    </div>
                  ) : (
                    <div key={i} className="p-4 bg-white border-b last:border-0">
                      <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 flex items-center gap-2 font-bold hover:underline">
                        <SafeIcon name="ExternalLink" className="w-4 h-4" />
                        Watch Demo {i + 1}
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="border-t pt-4 mt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Rate Session</label>
          <div className="flex gap-1">
            {[1,2,3,4,5].map(s=> (
              <button 
                key={s} 
                onClick={()=> updateTask({session_score: s})}
                className={`p-1 ${task.session_score >=s ? 'text-yellow-400' : 'text-gray-300'}`}
              >
                <SafeIcon name="Star" className="w-5 h-5 fill-current" />
              </button>
            ))}
          </div>
        </div>

        {task.require_video_submission && (
          <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 col-span-1 sm:col-span-2">
            <label className="block text-xs font-bold text-indigo-900 uppercase mb-2">Video Submission</label>
            {task.video_submission_description && task.video_submission_description !=='<p><br></p>' && (
              <div className="mb-3 text-sm text-indigo-800 bg-indigo-100/50 p-3 rounded-lg border border-indigo-200/50">
                <p className="font-semibold flex items-center gap-1.5 mb-1 text-xs">
                  <SafeIcon name="Info" className="w-3.5 h-3.5" />
                  Trainer's Instructions:
                </p>
                <div className="prose prose-sm max-w-none text-indigo-900 leading-relaxed" dangerouslySetInnerHTML={{__html: task.video_submission_description}} />
              </div>
            )}
            <input 
              type="url" 
              value={localVideoUrls[0] || ''}
              onChange={(e)=> setLocalVideoUrls([e.target.value])}
              onBlur={()=> updateTask({video_submission_url: localVideoUrls[0]})}
              placeholder="Paste video link..."
              className="w-full px-3 py-2 border border-indigo-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            />
            {localVideoUrls[0] && <p className="text-[10px] text-green-600 mt-1 font-medium flex items-center gap-1"><SafeIcon name="CheckCircle" className="w-3 h-3" /> Link saved</p>}
          </div>
        )}
      </div>

      <AnimatePresence>
        {expandedVideoUrl && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-[10005] flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setExpandedVideoUrl(null)}
          >
            <button 
              onClick={() => setExpandedVideoUrl(null)}
              className="absolute top-6 right-6 text-white p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all z-[10006]"
            >
              <SafeIcon name="X" className="w-8 h-8" />
            </button>
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative w-full max-w-6xl aspect-video"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe 
                src={expandedVideoUrl}
                className="absolute inset-0 w-full h-full rounded-2xl shadow-2xl border border-white/10"
                allow="fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
              />
              <div className="absolute -bottom-10 left-0 right-0 text-center text-white/40 font-black uppercase tracking-[0.2em] text-[10px] hidden sm:block">
                Click background to exit
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TaskCard;